// Persisted new-comparability proposal + TPA calculation payload
// (one-shot plan §"Persistence" and §"Exports and handoff").
//
// The proposal is the durable record of WHY the applied allocation looks the
// way it does: schema version, goal, targets, group controls, the selected
// scenario snapshot, assumptions, calculation version, input fingerprint, the
// requires-review acknowledgment (when one was needed), and the applied
// timestamp. It lives on the plan object (plan.newCompProposal), so it
// survives save/load, comparison slots, duplicate-plan flows, export/import,
// and current-state persistence with no second source of truth. The compiled
// calculation component remains the source of
// truth for the adopted allocation component itself.
//
// The calculation payload is built FROM already-computed results; no math
// happens here, and exporters must consume the payload rather than recompute.

import {
  canApplyComplianceStatus,
  isComplianceStatus,
  makeComplianceStatus,
  NEW_COMP_CALCULATION_VERSION,
} from './compliance/complianceStatus.js'
import { buildNewCompProposalModel } from './newCompProposalModel.js'

export const NEW_COMP_PROPOSAL_VERSION = 2

const isObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value)
const cleanStringArray = (value) => (Array.isArray(value)
  ? value.filter((entry) => typeof entry === 'string' && entry) : [])
const jsonRecord = (value) => (isObject(value) ? JSON.parse(JSON.stringify(value)) : {})
const nullableNumber = (value) => value !== null && value !== undefined && Number.isFinite(Number(value))
  ? Number(value) : null
const privateCount = (privateCollection, persistedCount) => Math.max(
  Array.isArray(privateCollection)
    ? privateCollection.length
    : (isObject(privateCollection) ? Object.keys(privateCollection).length : 0),
  Math.max(0, Number(persistedCount) || 0),
)
const cleanDisclosure = (value) => isObject(value)
  ? Object.fromEntries(Object.entries(value)
      .filter(([key]) => !['employeeIds', 'participantIds', 'memberIds', 'id', 'name'].includes(key))
      .map(([key, entry]) => [key, entry]))
  : null

const sanitizeComplianceSnapshot = (value) => {
  if (!isObject(value) || !isComplianceStatus(value.status)) {
    return makeComplianceStatus({ status: 'unavailable' })
  }
  return {
    ...value,
    reasons: (value.reasons || []).map(cleanDisclosure).filter(Boolean),
    assumptions: (value.assumptions || []).map(cleanDisclosure).filter(Boolean),
    limitations: (value.limitations || []).map(cleanDisclosure).filter(Boolean),
  }
}

const aggregateDetailSnapshot = (value) => {
  if (!isObject(value)) return {}
  const cross = isObject(value.crossTesting) ? value.crossTesting : null
  const combined = isObject(value.combined) ? value.combined : null
  return {
    ...(cross ? {
      crossTesting: {
        available: cross.available === true,
        status: cross.status ?? null,
        gatewayRoute: cross.gatewayRoute ?? null,
        gatewayRate: cross.gatewayRate ?? null,
        highestHceAllocationRate: cross.highestHceAllocationRate ?? null,
        gatewaySatisfied: cross.gatewaySatisfied ?? null,
        generalTestPasses: cross.generalTestPasses ?? null,
        planRatioPercent: cross.planRatioPercent ?? null,
        classificationThresholdPercent: cross.classificationThresholdPercent ?? null,
        abptRatio: cross.abptRatio ?? null,
        abptPass: cross.abptPass ?? null,
        allRateGroupsPass: cross.allRateGroupsPass ?? null,
        excludedNhceCount: cross.excludedNhceCount ?? null,
        excludedHceCount: cross.excludedHceCount ?? null,
        assumptionSet: cross.assumptionSet ?? null,
      },
    } : {}),
    ...(combined ? {
      combined: {
        available: combined.available === true,
        status: combined.status ?? null,
        aggregationOutcome: combined.aggregationOutcome ?? null,
        aggregationFacts: jsonRecord(combined.aggregationFacts),
        minimumParticipation: jsonRecord(combined.minimumParticipation),
        gatewayApplicable: combined.gatewayApplicable ?? null,
        gatewayMethod: combined.gatewayMethod ?? null,
        requiredGatewayPercent: combined.requiredGatewayPercent ?? null,
        highestHceAggregateRate: combined.highestHceAggregateRate ?? null,
        averageNhceDbRatePercent: combined.averageNhceDbRatePercent ?? null,
        allRateGroupsPass: combined.allRateGroupsPass ?? null,
        abptRatio: combined.abptRatio ?? null,
        abptPass: combined.abptPass ?? null,
        perPlan: isObject(combined.perPlan) ? {
          dcSeparatePasses: combined.perPlan.dcSeparatePasses ?? null,
          dcSeparateStatus: combined.perPlan.dcSeparate?.status ?? null,
          dbSeparatePasses: combined.perPlan.dbSeparate?.passes ?? null,
          dbSeparateStatus: combined.perPlan.dbSeparate?.status ?? null,
        } : null,
        assumptions: isObject(combined.assumptions) ? {
          summary: cleanStringArray(combined.assumptions.summary),
          limitations: cleanStringArray(combined.assumptions.limitations),
          estimatedFactorCount: Array.isArray(combined.assumptions.estimated)
            ? combined.assumptions.estimated.length : 0,
        } : null,
      },
    } : {}),
    coverage: isObject(value.coverage) ? {
      status: value.coverage.status ?? null,
      ratioPercent: value.coverage.ratioPercent ?? null,
      hceBenefiting: value.coverage.hceBenefiting ?? null,
      nhceBenefiting: value.coverage.nhceBenefiting ?? null,
      nonexcludableHceCount: value.coverage.nonexcludableHceCount ?? null,
      nonexcludableNhceCount: value.coverage.nonexcludableNhceCount ?? null,
    } : null,
    schedule415c: isObject(value.schedule415c) ? {
      status: value.schedule415c.status ?? null,
      totalReducedCents: value.schedule415c.totalReducedCents ?? null,
    } : null,
    deduction: isObject(value.deduction || value.deduction404a3)
      ? aggregateDeductionSnapshot(value.deduction || value.deduction404a3) : null,
    deduction404a7: isObject(value.deduction404a7)
      ? aggregateDeductionSnapshot(value.deduction404a7) : null,
    topHeavy: isObject(value.topHeavy) ? {
      status: value.topHeavy.status ?? null,
      modeledMinimumPercent: value.topHeavy.modeledMinimumPercent ?? null,
      shortfallCents: value.topHeavy.shortfallCents ?? null,
    } : null,
  }
}

function aggregateDeductionSnapshot(value) {
  return Object.fromEntries([
    'status', 'modeled', 'limitPercent', 'includedCount', 'excludedCount',
    'includedCompensationCents', 'excludedCompensationCents', 'limitCents',
    'employerContributionCents', 'headroomCents', 'triggered',
    'dcEmployerContributionCents', 'sixPercentCarveOutCents', 'excessOverCarveOutCents',
  ].filter((key) => value[key] !== undefined).map((key) => [key, value[key]]))
}

const sanitizeSuggestedCashBalance = (value) => {
  if (!isObject(value) || Object.keys(value).length === 0) return {}
  return {
    interestCreditRatePercent: Number(value.interestCreditRatePercent) || 0,
    nonTargetHceCbPercent: Number(value.nonTargetHceCbPercent) || 0,
    staffCbPercent: Number(value.staffCbPercent) || 0,
    targetCreditCount: privateCount(value.targetCreditsById, value.targetCreditCount),
    creditsByLabel: Array.isArray(value.creditsByLabel)
      ? value.creditsByLabel.filter(isObject).map((entry) => ({
          label: String(entry.label || ''),
          method: String(entry.method || ''),
          value: Number(entry.value) || 0,
        }))
      : [],
  }
}

const sanitizeSolverFacts = (value) => {
  const source = isObject(value) ? value : {}
  const options = isObject(source.options) ? source.options : {}
  return {
    ...(Number.isFinite(Number(source.fundingYears)) ? { fundingYears: Number(source.fundingYears) } : {}),
    ...(Number.isFinite(Number(source.interestCreditRate)) ? { interestCreditRate: Number(source.interestCreditRate) } : {}),
    ...(typeof source.dbPbgcCovered === 'string' ? { dbPbgcCovered: source.dbPbgcCovered } : {}),
    ...(isObject(source.aggregationFacts) ? { aggregationFacts: jsonRecord(source.aggregationFacts) } : {}),
    ...(typeof source.assumptionSet === 'string' ? { assumptionSet: source.assumptionSet } : {}),
    options: Object.fromEntries(['nonTargetHcePercent', 'maxStaffPercent', 'minStaffPercent', 'targetPsPercent',
      'nonTargetHceCbPercent', 'nonTargetHcePsPercent', 'staffCbPercent', 'maxStaffCbPercent', 'maxStaffPsPercent']
      .filter((key) => Number.isFinite(Number(options[key])))
      .map((key) => [key, Number(options[key])])),
  }
}

// ---------------------------------------------------------------------------
// Proposal schema
// ---------------------------------------------------------------------------

const normalizeScenarioSnapshot = (value) => {
  if (!isObject(value)) return null
  return {
    id: String(value.id || ''),
    label: String(value.label || ''),
    strategy: String(value.strategy || ''),
    optimality: value.optimality === 'lowest-cost-found' ? 'lowest-cost-found' : 'exact-minimum',
    staffPercent: Number(value.staffPercent) || 0,
    staffCbPercent: nullableNumber(value.staffCbPercent),
    staffPsPercent: nullableNumber(value.staffPsPercent),
    status: sanitizeComplianceSnapshot(value.status),
    totals: isObject(value.totals) ? { ...value.totals } : {},
    testStatuses: isObject(value.tests)
      ? Object.fromEntries(Object.entries(value.tests).map(([key, entry]) => [key, entry?.status ?? entry]))
      : (isObject(value.testStatuses) ? { ...value.testStatuses } : {}),
    participantCount: privateCount(value.perPerson, value.participantCount),
    perPerson: [],
    assignmentCount: privateCount(value.groupAssignments, value.assignmentCount),
    groupAssignments: {},
    objectiveResult: isObject(value.objectiveResult) ? {
      objective: value.objectiveResult.objective ?? null,
      targetCount: privateCount(value.objectiveResult.targetIds, value.objectiveResult.targetCount),
      requestedCents: Number(value.objectiveResult.requestedCents) || 0,
      achievedCents: Number(value.objectiveResult.achievedCents) || 0,
      optimizeFor: value.objectiveResult.optimizeFor ?? null,
    } : {},
    detail: aggregateDetailSnapshot(value.detail),
    suggestedCashBalance: sanitizeSuggestedCashBalance(value.suggestedCashBalance),
    explanation: String(value.explanation || ''),
  }
}

export function normalizeNewCompProposal(value) {
  if (!isObject(value)) return null
  const goal = isObject(value.goal) ? value.goal : {}
  const acknowledgment = isObject(value.acknowledgment) && value.acknowledgment.acknowledged === true
    ? {
        acknowledged: true,
        note: typeof value.acknowledgment.note === 'string' ? value.acknowledgment.note : '',
        at: typeof value.acknowledgment.at === 'string' ? value.acknowledgment.at : null,
      }
    : null
  return {
    version: Number.isInteger(value.version) ? value.version : NEW_COMP_PROPOSAL_VERSION,
    goal: {
      objective: typeof goal.objective === 'string' ? goal.objective : 'maximize-targets',
      targetCount: privateCount(goal.targetIds, goal.targetCount),
      targetIds: [],
      targetsById: {},
      budget: Number.isFinite(Number(goal.budget)) && goal.budget !== null ? Number(goal.budget) : null,
      optimizeFor: typeof goal.optimizeFor === 'string' ? goal.optimizeFor : 'lowest-employer-cost',
    },
    groupControls: Array.isArray(value.groupControls)
      ? value.groupControls.filter(isObject).map((control) => ({
          id: String(control.id || ''),
          label: String(control.label || ''),
          formula: String(control.formula || ''),
          fixed: nullableNumber(control.fixed),
          minimumPercent: Number(control.minimumPercent) || 0,
          maximumPercent: nullableNumber(control.maximumPercent),
          memberCount: privateCount(control.memberIds, control.memberCount),
        }))
      : [],
    selectedScenarioId: typeof value.selectedScenarioId === 'string' ? value.selectedScenarioId : null,
    selectedScenario: normalizeScenarioSnapshot(value.selectedScenario),
    alternativeScenarioIds: cleanStringArray(value.alternativeScenarioIds),
    scenarios: Array.isArray(value.scenarios)
      ? value.scenarios.map(normalizeScenarioSnapshot).filter(Boolean)
      : [],
    planFacts: jsonRecord(value.planFacts),
    solverFacts: sanitizeSolverFacts(value.solverFacts),
    reportInputs: jsonRecord(value.reportInputs),
    assumptions: Array.isArray(value.assumptions) ? value.assumptions.map(cleanDisclosure).filter(Boolean) : [],
    limitations: Array.isArray(value.limitations) ? value.limitations.map(cleanDisclosure).filter(Boolean) : [],
    calculationVersion: typeof value.calculationVersion === 'string'
      ? value.calculationVersion : NEW_COMP_CALCULATION_VERSION,
    rulesYear: Number.isInteger(value.rulesYear) ? value.rulesYear : null,
    inputFingerprint: typeof value.inputFingerprint === 'string' ? value.inputFingerprint : null,
    acknowledgment,
    appliedAt: typeof value.appliedAt === 'string' ? value.appliedAt : null,
  }
}

// Build the proposal to persist when a scenario is applied. Throws when the
// scenario's status forbids applying — callers must gate first.
export function buildNewCompProposal({ solveResult, scenario, acknowledgment = null, appliedAt = null }) {
  if (!scenario) throw new Error('A selected scenario is required to build a proposal.')
  const acknowledged = acknowledgment?.acknowledged === true
  if (!canApplyComplianceStatus(scenario.status, { acknowledged })) {
    throw new Error(`A scenario with status "${scenario.status?.status}" cannot be applied${scenario.status?.status === 'requires-review' ? ' without an acknowledgment' : ''}.`)
  }
  return normalizeNewCompProposal({
    version: NEW_COMP_PROPOSAL_VERSION,
    goal: solveResult?.goal,
    groupControls: solveResult?.groupControls ?? [],
    selectedScenarioId: scenario.id,
    selectedScenario: scenario,
    alternativeScenarioIds: (solveResult?.scenarios || [])
      .filter((entry) => entry.id !== scenario.id)
      .map((entry) => entry.id),
    scenarios: solveResult?.scenarios ?? [],
    planFacts: solveResult?.planFacts ?? {},
    solverFacts: solveResult?.solverFacts ?? {},
    reportInputs: solveResult?.reportInputs ?? solveResult?.solverFacts?.reportInputs ?? {},
    assumptions: scenario.status?.assumptions ?? [],
    limitations: scenario.status?.limitations ?? [],
    calculationVersion: solveResult?.calculationVersion ?? NEW_COMP_CALCULATION_VERSION,
    rulesYear: solveResult?.rulesYear ?? null,
    inputFingerprint: solveResult?.inputFingerprint ?? null,
    acknowledgment: acknowledged
      ? { acknowledged: true, note: acknowledgment.note || '', at: acknowledgment.at || appliedAt }
      : null,
    appliedAt,
  })
}

export function canApplyScenario(scenario, { acknowledged = false } = {}) {
  return canApplyComplianceStatus(scenario?.status, { acknowledged })
}

// ---------------------------------------------------------------------------
// TPA calculation payload (structured first; PDF/CSV/UI consume it)
// ---------------------------------------------------------------------------

export const NEW_COMP_PAYLOAD_FORMAT = 'retira-new-comparability-calculation-package'
export const NEW_COMP_PAYLOAD_VERSION = 2

// Stable section names — export tests assert these exact keys.
export const NEW_COMP_PAYLOAD_SECTIONS = Object.freeze([
  'metadata',
  'plan-elections',
  'readiness',
  'objective',
  'scenarios-compared',
  'per-person-allocations',
  'gateway-worksheet',
  'ebar-worksheet',
  'coverage-and-rate-groups',
  'section-415c-schedule',
  'deduction-screen',
  'top-heavy-screen',
  'proposal-summary',
  'funding-corridor',
  'multi-year-projections',
  'safe-harbor-alternatives',
  'minimum-participation-helper',
  'participant-disclosures',
  'assumptions-limitations-acknowledgments',
])

export function buildNewCompCalculationPayload({
  solveResult,
  scenario,
  planFacts = {},
  participants = [],
  proposal = null,
  planName = null,
  employerName = null,
  reportInputs = null,
  generatedAt = new Date().toISOString(),
} = {}) {
  if (!scenario) throw new Error('A scenario is required to build the calculation payload.')
  const detail = scenario.detail || {}
  const crossTesting = detail.crossTesting || {}
  const nameById = Object.fromEntries(participants.map((person) => [person.id, person.name || person.id]))
  const readiness = solveResult?.readiness || null
  const proposalModel = buildNewCompProposalModel({
    solveResult,
    scenario,
    participants,
    reportInputs: reportInputs ?? solveResult?.reportInputs ?? proposal?.reportInputs ?? {},
    planYear: solveResult?.rulesYear ?? planFacts.planYear ?? null,
  })

  const sections = {
    metadata: {
      planName,
      employerName,
      rulesYear: solveResult?.rulesYear ?? planFacts.planYear ?? null,
      calculationVersion: solveResult?.calculationVersion ?? NEW_COMP_CALCULATION_VERSION,
      inputFingerprint: solveResult?.inputFingerprint ?? null,
      generatedAt,
      disclaimer: 'Modeled proposal on documented assumptions. Not a formal nondiscrimination test, tax advice, or a TPA/actuarial determination.',
    },
    'plan-elections': {
      gatewayRoute: planFacts.gatewayRoute ?? null,
      gatewayMinimumMethod: planFacts.gatewayMinimumMethod ?? null,
      allocationFormula: planFacts.allocationFormula ?? null,
      otherwiseExcludableElection: planFacts.otherwiseExcludableElection === true,
      imputedDisparityElection: planFacts.imputedDisparityElection === true,
      rateBandingElection: planFacts.rateBandingElection === true,
      compensationDefinition: planFacts.compensationDefinition ?? null,
      eligibility: planFacts.eligibility ?? null,
      allocationConditions: planFacts.allocationConditions ?? null,
      aggregation: planFacts.aggregation ?? null,
      topHeavy: planFacts.topHeavy ?? null,
    },
    readiness: readiness
      ? {
          status: readiness.status,
          blockers: readiness.blockers,
          reviewItems: readiness.reviewItems,
          includedCount: readiness.includedCount,
          hceCount: readiness.hceCount,
          nhceCount: readiness.nhceCount,
        }
      : null,
    objective: scenario.objectiveResult,
    'scenarios-compared': (solveResult?.scenarios || []).map((entry) => ({
      id: entry.id,
      label: entry.label,
      selected: entry.id === scenario.id,
      status: entry.status?.status,
      staffPercent: entry.staffPercent,
      employerCostCents: entry.totals?.employerCostCents,
      targetProfitSharingCents: entry.totals?.targetProfitSharingCents,
      explanation: entry.explanation,
    })),
    'per-person-allocations': scenario.perPerson.map((row) => ({
      ...row,
      name: nameById[row.id] ?? row.id,
    })),
    'gateway-worksheet': {
      method: crossTesting.gatewayMinimumMethod ?? null,
      operativeRatePercent: crossTesting.gatewayRate ?? null,
      highestHceRatePercent: crossTesting.highestHceAllocationRate ?? null,
      satisfied: crossTesting.gatewaySatisfied ?? null,
      perPerson: (crossTesting.gateway?.perPerson || []).map((row) => ({
        ...row,
        name: nameById[row.id] ?? row.id,
      })),
    },
    'ebar-worksheet': {
      assumptionSet: crossTesting.assumptionSet ?? null,
      interest: crossTesting.interest ?? null,
      testingAge: crossTesting.testingAge ?? null,
      rows: (crossTesting.rows || []).map((row) => ({
        id: row.id,
        name: nameById[row.id] ?? row.id,
        isHCE: row.isHCE,
        compensation: row.comp,
        allocationRatePercent: row.allocationRate,
        testingAge: row.testingAge,
        ebar: row.ebar,
        abptEbar: row.abptEbar,
      })),
    },
    'coverage-and-rate-groups': {
      population: crossTesting.population ?? null,
      planRatioPercent: crossTesting.planRatioPercent ?? null,
      concentrationPercent: crossTesting.concentrationPercent ?? null,
      midpointPercent: crossTesting.midpointPercent ?? null,
      classificationThresholdPercent: crossTesting.classificationThresholdPercent ?? null,
      classificationThresholdBasis: crossTesting.classificationThresholdBasis ?? null,
      abptRatio: crossTesting.abptRatio ?? null,
      rateBands: crossTesting.rateBands ?? null,
      rateGroups: crossTesting.rateGroups ?? [],
      coverage: detail.coverage ?? null,
    },
    'section-415c-schedule': detail.schedule415c
      ? { rows: detail.schedule415c.rows, totalReducedCents: detail.schedule415c.totalReducedCents }
      : null,
    'deduction-screen': detail.deduction ?? detail.deduction404a3 ?? null,
    'top-heavy-screen': detail.topHeavy ?? null,
    'proposal-summary': {
      selected: proposalModel.selectedMetrics,
      scenarioPortfolio: proposalModel.scenarioPortfolio,
      designPortfolio: proposalModel.designPortfolio,
      marginalTaxRatePercent: proposalModel.inputs.marginalTaxRatePercent,
    },
    'funding-corridor': proposalModel.fundingCorridor,
    'multi-year-projections': {
      assumptions: proposalModel.projectionAssumptions,
      participants: proposalModel.projections,
    },
    'safe-harbor-alternatives': proposalModel.safeHarborAlternatives,
    'minimum-participation-helper': proposalModel.minimumParticipationHelper,
    'participant-disclosures': proposalModel.participantDisclosures,
    'assumptions-limitations-acknowledgments': {
      assumptions: scenario.status?.assumptions ?? [],
      limitations: scenario.status?.limitations ?? [],
      modeledElections: crossTesting.modeledElections ?? [],
      acknowledgment: proposal?.acknowledgment ?? null,
      statusVocabulary: 'passes | fails | requires-review | unavailable | not-applicable',
    },
  }
  // Combined DB/DC designs carry the full combined result and the §404(a)(7)
  // screen as additional sections (additive; the stable DC section names are
  // unchanged).
  if (detail.combined) {
    sections['combined-dbdc'] = detail.combined
    sections['combined-deduction-404a7'] = detail.deduction404a7 ?? null
  }
  return {
    format: NEW_COMP_PAYLOAD_FORMAT,
    version: NEW_COMP_PAYLOAD_VERSION,
    sections,
  }
}

// ---------------------------------------------------------------------------
// CSV projection of the payload (stable section headers; no recomputation)
// ---------------------------------------------------------------------------

const csvEscape = (value) => {
  const text = value === null || value === undefined ? '' : String(value)
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}
const csvRow = (cells) => cells.map(csvEscape).join(',')
const dollars = (cents) => (cents !== null && cents !== undefined && cents !== '' && Number.isFinite(Number(cents))
  ? (Number(cents) / 100).toFixed(2) : '')
const pct = (value) => (Number.isFinite(Number(value)) ? Number(value).toFixed(4) : '')

export function newCompPayloadToCsv(payload) {
  const sections = payload?.sections || {}
  const lines = []
  const section = (name) => lines.push('', `# section: ${name}`)

  section('metadata')
  const metadata = sections.metadata || {}
  lines.push(csvRow(['field', 'value']))
  for (const key of ['planName', 'employerName', 'rulesYear', 'calculationVersion', 'inputFingerprint', 'generatedAt', 'disclaimer']) {
    lines.push(csvRow([key, metadata[key]]))
  }

  section('plan-elections')
  const elections = sections['plan-elections'] || {}
  lines.push(csvRow(['field', 'value']))
  for (const [key, value] of Object.entries(elections)) {
    lines.push(csvRow([key, typeof value === 'object' && value !== null ? JSON.stringify(value) : value]))
  }

  section('readiness')
  lines.push(csvRow(['kind', 'code', 'message', 'employee_ids']))
  for (const kind of ['blockers', 'reviewItems']) {
    for (const entry of sections.readiness?.[kind] || []) {
      lines.push(csvRow([kind === 'blockers' ? 'blocker' : 'review', entry.code, entry.message, (entry.employeeIds || []).join('; ')]))
    }
  }

  section('scenarios-compared')
  lines.push(csvRow(['id', 'selected', 'status', 'staff_percent', 'employer_cost', 'target_profit_sharing', 'explanation']))
  for (const entry of sections['scenarios-compared'] || []) {
    lines.push(csvRow([entry.id, entry.selected ? 'yes' : 'no', entry.status, pct(entry.staffPercent), dollars(entry.employerCostCents), dollars(entry.targetProfitSharingCents), entry.explanation]))
  }

  section('per-person-allocations')
  lines.push(csvRow([
    'id', 'name', 'role', 'group_id', 'compensation',
    'match_excluded_from_gateway', 'safe_harbor_nec', 'other_nec', 'qnec', 'allocated_forfeiture',
    'ps_requested', 'ps_reduced_415c', 'ps_final', 'ps_percent', 'employer_total',
  ]))
  for (const row of sections['per-person-allocations'] || []) {
    lines.push(csvRow([
      row.id, row.name, row.role, row.groupId, dollars(row.compensationCents),
      dollars(row.matchCents), dollars(row.safeHarborNonelectiveCents), dollars(row.otherNonelectiveCents),
      dollars(row.qnecCents), dollars(row.forfeitureCents),
      dollars(row.profitSharingRequestedCents), dollars(row.profitSharingReducedCents),
      dollars(row.profitSharingCents), pct(row.profitSharingPercent), dollars(row.employerCents),
    ]))
  }

  section('gateway-worksheet')
  const gateway = sections['gateway-worksheet'] || {}
  lines.push(csvRow(['method', gateway.method, 'operative_rate_percent', pct(gateway.operativeRatePercent), 'satisfied', gateway.satisfied]))
  lines.push(csvRow(['id', 'name', 'benefiting', 'one_third_required', 'five_percent_required', 'required', 'supplied', 'shortfall']))
  for (const row of gateway.perPerson || []) {
    lines.push(csvRow([row.id, row.name, row.benefiting ? 'yes' : 'no',
      row.oneThirdRequiredDollars?.toFixed?.(2), row.fivePercentRequiredDollars?.toFixed?.(2),
      row.requiredDollars?.toFixed?.(2), row.suppliedDollars?.toFixed?.(2), row.shortfallDollars?.toFixed?.(2)]))
  }

  section('ebar-worksheet')
  const ebar = sections['ebar-worksheet'] || {}
  lines.push(csvRow(['assumption_set', ebar.assumptionSet, 'interest', ebar.interest, 'testing_age_default', ebar.testingAge]))
  lines.push(csvRow(['id', 'name', 'hce', 'compensation', 'allocation_rate_percent', 'testing_age', 'ebar_percent', 'abpt_ebar_percent']))
  for (const row of ebar.rows || []) {
    lines.push(csvRow([row.id, row.name, row.isHCE ? 'yes' : 'no', Number(row.compensation).toFixed(2), pct(row.allocationRatePercent), row.testingAge, pct(row.ebar), pct(row.abptEbar)]))
  }

  section('coverage-and-rate-groups')
  const coverage = sections['coverage-and-rate-groups'] || {}
  lines.push(csvRow(['plan_ratio_percent', pct(coverage.planRatioPercent), 'midpoint_percent', pct(coverage.midpointPercent), 'threshold_percent', pct(coverage.classificationThresholdPercent), 'threshold_basis', coverage.classificationThresholdBasis, 'abpt_ratio', pct(coverage.abptRatio)]))
  lines.push(csvRow(['rate_group_hce', 'rate_group_ebar', 'ratio_percent', 'threshold_percent', 'passes', 'basis']))
  for (const group of coverage.rateGroups || []) {
    lines.push(csvRow([group.hceId, pct(group.rateGroupEbar), pct(group.ratioPercent), pct(group.thresholdPercent), group.passes ? 'yes' : 'no', group.basis]))
  }

  section('section-415c-schedule')
  lines.push(csvRow(['id', 'cap', 'existing_additions', 'ps_requested', 'ps_reduced', 'ps_final', 'total_additions', 'remaining_headroom', 'at_limit']))
  for (const row of sections['section-415c-schedule']?.rows || []) {
    lines.push(csvRow([row.id, dollars(row.capCents), dollars(row.fixedTotalCents), dollars(row.profitSharingRequestedCents), dollars(row.profitSharingReducedCents), dollars(row.profitSharingFinalCents), dollars(row.totalAdditionsCents), dollars(row.remainingHeadroomCents), row.atLimit ? 'yes' : 'no']))
  }

  section('deduction-screen')
  const deduction = sections['deduction-screen'] || {}
  lines.push(csvRow(['status', deduction.status, 'included_count', deduction.includedCount, 'included_compensation', dollars(deduction.includedCompensationCents), 'limit', dollars(deduction.limitCents), 'employer_contributions', dollars(deduction.employerContributionCents), 'headroom', dollars(deduction.headroomCents)]))

  section('top-heavy-screen')
  const topHeavy = sections['top-heavy-screen'] || {}
  lines.push(csvRow(['status', topHeavy.status, 'modeled_minimum_percent', topHeavy.modeledMinimumPercent, 'shortfall', dollars(topHeavy.shortfallCents)]))

  section('proposal-summary')
  const proposalSummary = sections['proposal-summary'] || {}
  lines.push(csvRow(['scenario', 'status', 'employer_cost', 'owner_benefit', 'staff_cost', 'estimated_tax_savings', 'after_tax_cost', 'owner_share_percent', 'cost_per_owner_benefit_dollar']))
  for (const entry of proposalSummary.designPortfolio || proposalSummary.scenarioPortfolio || []) {
    lines.push(csvRow([entry.label, entry.status, dollars(entry.employerCostCents), dollars(entry.ownerBenefitCents), dollars(entry.staffCostCents), dollars(entry.estimatedTaxSavingsCents), dollars(entry.estimatedAfterTaxCostCents), pct(entry.ownerBenefitSharePercent), entry.employerCostPerOwnerBenefitDollar?.toFixed?.(4)]))
  }

  section('funding-corridor')
  const corridor = sections['funding-corridor'] || {}
  lines.push(csvRow(['status', corridor.status, 'minimum', dollars(corridor.minimumCents), 'recommended', dollars(corridor.recommendedCents), 'maximum', dollars(corridor.maximumCents), 'source', corridor.source, 'as_of', corridor.asOf, 'note', corridor.note, 'limitation', corridor.limitation]))

  section('multi-year-projections')
  const projections = sections['multi-year-projections'] || {}
  lines.push(csvRow(['id', 'name', 'role', 'year', 'opening_cb_balance', 'cb_credit', 'interest_credit', 'ending_cb_balance', 'dc_employer', 'cumulative_dc_employer']))
  for (const participant of projections.participants || []) {
    for (const row of participant.years || []) {
      lines.push(csvRow([participant.id, participant.name, participant.role, row.year, dollars(row.openingBalanceCents), dollars(row.cashBalanceCreditCents), dollars(row.interestCreditCents), dollars(row.endingBalanceCents), dollars(row.dcEmployerCents), dollars(row.cumulativeDcEmployerCents)]))
    }
  }

  section('safe-harbor-alternatives')
  lines.push(csvRow(['design', 'formula', 'modeled_employer_cost', 'status', 'limitation']))
  for (const entry of sections['safe-harbor-alternatives'] || []) {
    lines.push(csvRow([entry.label, entry.formula, dollars(entry.employerCostCents), entry.status, entry.limitation]))
  }

  section('minimum-participation-helper')
  const helper = sections['minimum-participation-helper'] || {}
  lines.push(csvRow(['benefiting', helper.benefitingCount, 'required', helper.requiredBenefiting, 'additional_needed', helper.additionalBenefitingNeeded, 'meaningful_rate_percent', helper.meaningfulAccrualRatePercent, 'limitation', helper.limitation]))
  lines.push(csvRow(['candidate_id', 'current_accrual_rate_percent', 'gap_percent', 'approximate_increment']))
  for (const entry of helper.candidates || []) {
    lines.push(csvRow([entry.id, pct(entry.currentAccrualRatePercent), pct(entry.gapToMeaningfulRatePercent), dollars(entry.approximateIncrementCents)]))
  }

  section('participant-disclosures')
  const participantDisclosures = sections['participant-disclosures'] || {}
  lines.push(csvRow(['topic', 'value']))
  for (const [key, value] of Object.entries(participantDisclosures)) lines.push(csvRow([key, value]))

  if (sections['combined-dbdc']) {
    const combined = sections['combined-dbdc']
    section('combined-dbdc')
    lines.push(csvRow(['status', combined.status, 'aggregation_outcome', combined.aggregationOutcome,
      'required_gateway_percent', pct(combined.requiredGatewayPercent),
      'highest_hce_aggregate_percent', pct(combined.highestHceAggregateRate),
      'abpt_ratio', pct(combined.abptRatio),
      'min_participation', `${combined.minimumParticipation?.benefitingCount}/${combined.minimumParticipation?.requiredBenefiting}`]))
    lines.push(csvRow(['id', 'hce', 'cb_credit', 'dc_allocation', 'aggregate_rate_percent', 'aggregate_ebar', 'most_valuable_ebar', 'accrual_rate_percent']))
    for (const row of combined.rows || []) {
      lines.push(csvRow([row.id, row.isHCE ? 'yes' : 'no', Number(row.cbCredit).toFixed(2), Number(row.dcAllocation).toFixed(2),
        pct(row.aggregateAllocationRatePercent), pct(row.aggregateEbar), pct(row.mostValuableEbar), pct(row.accrualRatePercent)]))
    }
    section('combined-deduction-404a7')
    const a7 = sections['combined-deduction-404a7'] || {}
    lines.push(csvRow(['status', a7.status, 'triggered', a7.triggered ? 'yes' : 'no',
      'dc_employer', dollars(a7.dcEmployerContributionCents),
      'six_percent_carve_out', dollars(a7.sixPercentCarveOutCents),
      'excess_over_carve_out', dollars(a7.excessOverCarveOutCents)]))
  }

  section('assumptions-limitations-acknowledgments')
  const closing = sections['assumptions-limitations-acknowledgments'] || {}
  lines.push(csvRow(['kind', 'code', 'text']))
  for (const assumption of closing.assumptions || []) lines.push(csvRow(['assumption', assumption.code, `${assumption.label}: ${assumption.value}`]))
  for (const limitation of closing.limitations || []) lines.push(csvRow(['limitation', limitation.code, limitation.message]))
  for (const election of closing.modeledElections || []) lines.push(csvRow(['modeled-election', election.election, election.citation]))
  if (closing.acknowledgment) lines.push(csvRow(['acknowledgment', closing.acknowledgment.at, closing.acknowledgment.note || 'acknowledged']))

  return lines.slice(1).join('\n')
}
